/**
 * Side charts: breach outflow and flooded area against time.
 *
 * Both series are thinned server-side (Hard Rule 8) - the routing step is 10 s,
 * so 24 h is 8,640 samples for a chart a few hundred pixels wide.
 */

import { useQuery } from '@tanstack/react-query'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { api } from '../api/client'
import { useAppStore } from '../store/useAppStore'
import { formatElapsedHours } from '../lib/formatElapsed'
import { C, fs } from '../design/tokens'

const AXIS = { fontSize: fs(12), fill: C.textFaint }
const TOOLTIP = {
  background: C.bg,
  border: '1px solid var(--ink-grid)',
  fontSize: fs(12),
}

function Panel({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children: React.ReactNode
}) {
  return (
    <section className="rounded-md border border-slate-800 bg-slate-900 p-3">
      <h3 className="text-xs font-medium uppercase tracking-wider text-slate-400">
        {title}
      </h3>
      {subtitle && <p className="text-12 text-slate-500">{subtitle}</p>}
      <div className="mt-2 h-32">{children}</div>
    </section>
  )
}

export function Hydrograph({ runId }: { runId: string }) {
  const frameIndex = useAppStore((s) => s.frameIndex)

  // Only a QuickFlood run routes a breach hydrograph; an imported result carries none.
  const runs = useQuery({ queryKey: ['runs'], queryFn: api.runs })
  const solver = runs.data?.find((r) => r.id === runId)?.solver
  const hydrograph = useQuery({
    queryKey: ['hydrograph', runId],
    queryFn: () => api.hydrograph(runId),
    enabled: solver === 'quickflood',
  })
  const areaSeries = useQuery({
    queryKey: ['area-series', runId],
    queryFn: () => api.areaSeries(runId),
  })

  if (hydrograph.isError || (solver !== undefined && solver !== 'quickflood')) return null

  const nowHours =
    areaSeries.data && areaSeries.data.length > frameIndex
      ? areaSeries.data[frameIndex].time_s / 3600
      : null

  return (
    <div className="space-y-3">
      <Panel
        title="Breach outflow"
        subtitle={
          hydrograph.data
            ? `peak ${Math.round(hydrograph.data.peak_cumecs).toLocaleString()} m³/s at T + ${(
                hydrograph.data.time_to_peak_s / 3600
              ).toFixed(2)} h`
            : undefined
        }
      >
        {hydrograph.isPending && <p className="text-xs text-slate-500">Loading&hellip;</p>}
        {hydrograph.data && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={hydrograph.data.times_s.map((time, index) => ({
                hours: time / 3600,
                q: hydrograph.data.q_total_cumecs[index],
              }))}
              margin={{ top: 4, right: 6, bottom: 0, left: -8 }}
            >
              <CartesianGrid stroke={C.grid} />
              <XAxis
                dataKey="hours"
                tick={AXIS}
                stroke={C.grid}
                tickFormatter={formatElapsedHours}
              />
              <YAxis
                tick={AXIS}
                stroke={C.grid}
                width={48}
                tickFormatter={(value: number) => `${Math.round(value / 1000)}k`}
              />
              <Tooltip
                contentStyle={TOOLTIP}
                labelFormatter={(value: number) => `T + ${value.toFixed(2)} h`}
                formatter={(value: number) => [
                  `${Math.round(value).toLocaleString()} m³/s`,
                  'discharge',
                ]}
              />
              {nowHours !== null && (
                <ReferenceLine x={nowHours} stroke={C.warn} strokeDasharray="3 3" />
              )}
              <Area
                type="monotone"
                dataKey="q"
                stroke={C.accent}
                fill={C.accentDim}
                strokeWidth={1.5}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Panel>

      <Panel title="Flooded area over time" subtitle="km² above the wet threshold">
        {areaSeries.isPending && <p className="text-xs text-slate-500">Loading&hellip;</p>}
        {areaSeries.data && areaSeries.data.length === 0 && (
          <p className="text-xs text-slate-500">
            This run stored no area series. It predates the results table.
          </p>
        )}
        {areaSeries.data && areaSeries.data.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={areaSeries.data.map((point) => ({
                hours: point.time_s / 3600,
                area: point.flooded_area_km2,
              }))}
              margin={{ top: 4, right: 6, bottom: 0, left: -8 }}
            >
              <CartesianGrid stroke={C.grid} />
              <XAxis
                dataKey="hours"
                tick={AXIS}
                stroke={C.grid}
                tickFormatter={formatElapsedHours}
              />
              <YAxis tick={AXIS} stroke={C.grid} width={48} />
              <Tooltip
                contentStyle={TOOLTIP}
                labelFormatter={(value: number) => `T + ${value.toFixed(2)} h`}
                formatter={(value: number) => [`${value.toFixed(1)} km²`, 'flooded']}
              />
              {nowHours !== null && (
                <ReferenceLine x={nowHours} stroke={C.warn} strokeDasharray="3 3" />
              )}
              <Line
                type="monotone"
                dataKey="area"
                stroke={C.accent}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Panel>
    </div>
  )
}
